// Vendors
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

// Components
import { Trans, InputNumeric } from '../common';

// Selectors
import { storeTotalFeesAndDonations } from '../../reducers/agency/dlr/dlrActions';
// Styles
import './fees-donations.scss';

// Types
import { formikInjectedPropsTypes } from '../../lib/validation/propTypes/formikPropTypes';

const Fees = ({
  feeList,
  subTotalAmountDue = '74.75',
  showSubTotal = true,
  formikProps,
  setDonationValue,
  classname = '',
  classnameItem = '',
  tpwdAltFeesFootnote = false,
  storeTotalFeesAndDonations
}) => {
  const values = (formikProps && formikProps.values) || {};

  // Added to capture fee calculation data and send to backend to analyze fee discrepancies.
  // Will be printed under header - Frontend log for payment analysis
  let totalFeeAndDonationLog = `--Begin Fees component render--`;

  /**
   * Helper function to calculate total with a specific field update
   * Used during handleBlur to provide immediate total calculation
   */
  const calculateUpdatedTotal = (fieldId, newValue) => {
    let totalDonationValue = 0;
    // Added to capture fee calculation data and send to backend to analyze fee discrepancies.
    totalFeeAndDonationLog += ` Calculating updated total with fieldId: ${fieldId} and newValue: ${newValue}`;
      
    feeList.forEach(fee => {
      if (fee.type?.editable && fee.type?.id) {
        // Use newValue for the field being updated, otherwise use current value
        const donationValue = fee.type.id === fieldId 
          ? newValue 
          : values[fee.type.id];
        
        if (donationValue !== '' && donationValue != null && !isNaN(parseFloat(donationValue))) {
          const parsedValue = parseFloat(donationValue);
          if (parsedValue !== 0) {
            totalFeeAndDonationLog += ` Adding donation value from field ${fee.type?.id}: ${parsedValue}`;
            totalDonationValue += parsedValue;
          }
        }
      }
    }); 
    
    if (showSubTotal) {
      totalFeeAndDonationLog += ` Applying discount and calculating total: ${parseFloat(subTotalAmountDue)} + ${totalDonationValue} - 1`;
       
      return (parseFloat(subTotalAmountDue) + totalDonationValue - 1).toFixed(2);
    }
    totalFeeAndDonationLog += ` Applying discount and calculating total: ${parseFloat(subTotalAmountDue)} + ${totalDonationValue}`;
    storeTotalFeesAndDonations(totalFeeAndDonationLog, true);
    return (parseFloat(subTotalAmountDue) + totalDonationValue).toFixed(2);
  };

  /* Memoized to prevent recalculation on every renders*/
  const subTotal = useMemo(() => {
    let totalDonationValue = 0;
    // Only iterate through editable donation fields from feeList
    feeList.forEach(fee => {
       if (fee.type?.id) {
        const donationValue = values[fee.type.id];
        if (donationValue !== '' && donationValue != null && !isNaN(parseFloat(donationValue))) {
          const parsedValue = parseFloat(donationValue);
          if (parsedValue !== 0) {
            totalDonationValue += parsedValue;
          }
        }
      }
    });

    // Apply discount and calculate total
    if (showSubTotal) {
      return (parseFloat(subTotalAmountDue) + totalDonationValue - 1).toFixed(2);
    }
    return (parseFloat(subTotalAmountDue) + totalDonationValue).toFixed(2);
  }, [feeList, values, subTotalAmountDue, showSubTotal]);


  const handleBlur = async (fee, e) => {
    let value = !e.target.value
      ? 0
      : Number.parseFloat(e.target.value).toFixed(2);
    // Prevent negative values
    // if (parseFloat(value) < 0) {
    //   value = '0.00';
    // }
     // Added to capture fee calculation data and send to backend to analyze fee discrepancies.
    totalFeeAndDonationLog += ` Handling blur for fieldId: ${fee.type?.id} with value: ${value}`;
    formikProps.setFieldValue(e.target.name, value);
    if (setDonationValue) {
      totalFeeAndDonationLog += ` Setting donation value for fieldId: ${fee.type?.id} with value: ${value}`;
      // Calculate total with the new value for immediate feedback
      // Note: subTotal will update on next render via useMemo
      const updatedTotal = calculateUpdatedTotal(fee.type?.id, value);

      // Collect ALL donation values from Formik (with the current field's new value)
      const allDonationValues = {};
      feeList.forEach(feeItem => {
        if (feeItem.type?.editable && feeItem.type?.id) {
          const fieldValue = feeItem.type.id === fee.type?.id 
            ? value 
            : values[feeItem.type.id];
          // Convert to proper number format, default to 0 if empty/invalid
          const numValue = fieldValue !== '' && fieldValue != null && !isNaN(parseFloat(fieldValue))
            ? Number.parseFloat(fieldValue).toFixed(2)
            : '0';
          allDonationValues[feeItem.type.id] = numValue;
        }
      });
      
      totalFeeAndDonationLog += ` Syncing all donation values: ${JSON.stringify(allDonationValues)}`;
      storeTotalFeesAndDonations(totalFeeAndDonationLog, true);
      
      // Sync ALL Formik donation values to Redux, not just the changed field
      await setDonationValue(fee.type?.id, value, updatedTotal, allDonationValues);
    }
    formikProps.handleBlur(e);

  }

  const list = feeList.map((fee, key) => {
    let feeType;

    if (fee.type?.editable) {
      feeType = (
        <div className="value editable flex-item">
          <InputNumeric
            name={`${fee.type.id}`}
            formikProps={formikProps}
            placeholder="0.00"
            onBlur={e => handleBlur(fee, e)}
            autoComplete="off"
            onChange={e => {
              let value = e.target.value.replace(/[^0-9.]/g, ''); // Remove non-numeric characters except decimal
              
              // Handle multiple decimal points - keep only the first one
              const parts = value.split('.');
              if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
              }
              
              // Limit to 2 decimal places
              if (parts.length === 2 && parts[1].length > 2) {
                value = parts[0] + '.' + parts[1].substring(0, 2);
              }
              
              // Prevent negative values
              const numericValue = parseFloat(value);
              if (!isNaN(numericValue) && numericValue < 0) {
                value = '0';
              }
              
              formikProps.setFieldValue(e.target.name, value);
            }}
          />
        </div>
      );
    } else {
      feeType = (
        <div className={classnames('value', 'flex-item', classnameItem)}>
          {fee.value}
        </div>
      );
    }

    return (
      <li
        label="Fees List"
        key={key}
        className={classnames('feeItem', 'flex-row-wrap', classnameItem)}
      >
        <div className={classnames('key', 'flex-item', classnameItem)}>
          {fee.label}
        </div>
        {feeType}
      </li>
    );
  });

  return (
    <ul className="feesContainer" aria-label="Fees List">
      {list}
      {showSubTotal && (
        <>
          <li key="subtotals" className="feeItem subtotals flex-row-wrap">
            <div className="key flex-item">
              <Trans file="Labels" id="subtotal" />,
            </div>
            <div className="value flex-item">{subTotal}</div>
          </li>
          <li className="feeItem spacer"></li>
          <li key="discount" className="feeItem discount flex-row-wrap">
            <div className="key flex-item">
              <Trans file="Labels" id="onlineDiscount" />
            </div>
            <div className="value flex-item">-1.00</div>
          </li>
        </>
      )}
      <li key="total" className="feeItem total flex-row-wrap">
        <div className={classnames('key', 'flex-item', classname)}>
          <Trans file="Labels" id="total" />
          {tpwdAltFeesFootnote ? '*' : ''}
        </div>
        <div className={classnames('value', 'flex-item', classname)}>
          <span>$</span>
          {formikProps
            ? subTotal
            : `${parseFloat(subTotalAmountDue).toFixed(2)}`}
        </div>
      </li>
    </ul>
  );
};

Fees.propTypes = {
  feeList: PropTypes.array.isRequired,
  formikProps: formikInjectedPropsTypes.isRequired,
  subTotalAmountDue: PropTypes.string.isRequired,
  showSubTotal: PropTypes.bool.isRequired,
  setDonationValue: PropTypes.func.isRequired,
  classname: PropTypes.string,
  classnameItem: PropTypes.string,
  tpwdAltFeesFootnote: PropTypes.bool,
  storeTotalFeesAndDonations: PropTypes.func,
};

export default Fees;
